import React, { useState, useEffect } from "react";
import Table from "./components/Table";
import Card from "components/card";
import { useTenant } from "@contexts/TenantContext";
import { userDataService } from "@services/userDataService";
import { EnhancedUser } from "types/profile.types";

const UserOverview = () => {
  const { currentTenant, currentTenantId, currentUser } = useTenant();
  const [users, setUsers] = useState<EnhancedUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch users when tenant changes
  useEffect(() => {
    if (currentTenantId) {
      fetchUsersForTenant(currentTenantId);
    } else {
      setUsers([]);
    }
  }, [currentTenantId]);

  const fetchUsersForTenant = async (tenantId: string, silent = false) => {
    if (!silent) {
      setLoading(true);
    }
    setError(null);

    try {
      const enhancedUsers = await userDataService.fetchUsersForTenant(tenantId);
      setUsers(enhancedUsers);
    } catch (err) {
      console.error('Error fetching users:', err);
      if (!silent) {
        setError('Failed to load users. Please try again.');
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  // Silent refresh for background updates (doesn't show loading spinner)
  const refreshUsers = () => {
    if (currentTenantId) {
      fetchUsersForTenant(currentTenantId, true);
    }
  };

  // Update a single user in the list (optimized - doesn't refetch all users)
  const updateSingleUser = async (uid: string) => {
    try {
      const updatedUser = await userDataService.fetchSingleUser(uid);
      if (updatedUser) {
        setUsers(prevUsers =>
          prevUsers.map(user => (user.uid === uid ? updatedUser : user))
        );
      }
    } catch (error) {
      console.error('Error updating single user:', error);
    }
  };

  // Convert EnhancedUser[] to table format
  const tableData = users.map(user => {
    const isCurrentUser = user.uid === currentUser?.uid;
    const displayName = isCurrentUser ? `${user.displayName} (You)` : user.displayName;
    const email = user.profile?.email || user.authEmail || (isCurrentUser ? currentUser?.email : '') || '';

    return {
    name: [displayName, user.uid], // [name, uid for avatar]
    email,
    username: isCurrentUser ? 'You' : user.displayName, // Show 'You' instead of UID
    date: user.profile?.createdAt ? new Date(user.profile.createdAt).toLocaleDateString() : 'N/A',
    type: user.profile?.mentor ? 'Mentor' : 'Mentee',
    actions: null, // Will be handled by the table component
    uid: user.uid, // Add UID for actions
    status: user.status
  };
  });

  if (!currentTenant) {
    return (
      <Card extra={"w-full h-full mt-3 flex items-center justify-center"}>
        <div className="text-center py-8">
          <p className="text-gray-500 dark:text-gray-400">
            Please select a tenant to view users.
          </p>
        </div>
      </Card>
    );
  }

  if (loading) {
    return (
      <Card extra={"w-full h-full mt-3 flex items-center justify-center"}>
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-500 mx-auto mb-4"></div>
          <p className="text-gray-500 dark:text-gray-400">Loading users...</p>
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card extra={"w-full h-full mt-3 flex items-center justify-center"}>
        <div className="text-center py-8">
          <p className="text-red-500 mb-4">{error}</p>
          <button
            onClick={() => currentTenantId && fetchUsersForTenant(currentTenantId)}
            className="px-4 py-2 bg-brand-500 text-white rounded-lg hover:bg-brand-600 transition-colors"
          >
            Retry
          </button>
        </div>
      </Card>
    );
  }

  return (
    <div className="mt-3">
      <Card extra={"w-full h-full"}>
        <Table
          tableData={tableData}
          tenantId={currentTenantId}
          tenantName={currentTenant.title}
          onRefresh={refreshUsers}
          onUpdateSingleUser={updateSingleUser}
          users={users}
        />
      </Card>
    </div>
  );
};

export default UserOverview;
